import { useCallback, useEffect, useRef } from "react";
import type { MarkRunOnPlan, RunMarkPosition, UseRunMarkArgs } from "./types";

export const useRunMark = ({
  getRunMarks,
  setMarks,
  dwellMs,
}: UseRunMarkArgs): MarkRunOnPlan => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getRef = useRef(getRunMarks);
  getRef.current = getRunMarks;
  const setRef = useRef(setMarks);
  setRef.current = setMarks;

  // A mark left running after the panel goes is a timer writing into a plan
  // that is no longer there.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return useCallback(
    (circuitId) => {
      // The last mark stands down rather than fighting: a second move must not
      // be cut short by the first one's timer.
      if (timerRef.current) clearTimeout(timerRef.current);
      const marks: RunMarkPosition[] = circuitId
        ? getRef.current(circuitId)
        : [];
      if (!marks.length) {
        setRef.current(null);
        return;
      }
      setRef.current(marks);
      timerRef.current = setTimeout(() => {
        // Only if the marks are still ours: a removal he hovered in the
        // meantime owns them now, and taking them off him is worse than
        // leaving ours up a moment too long.
        setRef.current((current) => (current === marks ? null : current));
      }, dwellMs);
    },
    [dwellMs],
  );
};
